// The small dialogs on Claims → Runs: deleting a run, recording its ICN or
// claim number, and confirming a file before it goes to GAMMIS. (Editing a
// line moved to LinePanel.js, a side panel.)
//
// Moved out of pages/finance/BillingRun.js on 2026-10-20. Each only collects
// input; the page writes it through lib/billing/runs.js.

import { fmt$, fmt$2, fmtDate } from './format';

// Sending is the one step that cannot be taken back: once the file is on
// Gainwell's server it is a filing. So it is never one click — this says what
// is about to leave (the week, how many claims, what they charge, and whether
// GAMMIS will adjudicate it) and waits to be told. (2026-10-22.)
export function SendToGammisDialog({ sub, run, sending, onCancel, onConfirm }) {
  const live = sub.usage_indicator === 'P';
  const count = Number(sub.claim_count) || 0;
  return (
      <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && !sending && onCancel()}>
        <div className="modal" style={{ maxWidth: 460 }} onMouseDown={e => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">Send to GAMMIS?</div>
            <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={sending}>✕</button>
          </div>
          <div className="modal-body">
            <div style={{ padding: '10px 14px', background: 'var(--bg2)', borderRadius: 8, fontSize: 13, lineHeight: 1.7, marginBottom: 10 }}>
              <div><strong>{run ? `${fmtDate(run.date_from)} – ${fmtDate(run.date_to)}` : 'Billing run'}</strong></div>
              <div>{count} claim{count === 1 ? '' : 's'} · {fmt$2(sub.total_charge)} charged</div>
              <div style={{ fontSize: 11.5, color: 'var(--text3)' }}>{sub.file_name}</div>
            </div>
            {live ? (
              <p style={{ fontSize: 13, color: 'var(--danger)', fontWeight: 500, marginBottom: 6 }}>
                This files real claims with Georgia Medicaid. Once it is on Gainwell's server it cannot be recalled.
              </p>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 6 }}>
                This is a <strong>test</strong> file (ISA15 = {sub.usage_indicator || '?'}). GAMMIS will check it but will not pay it.
              </p>
            )}
            <p style={{ fontSize: 12, color: 'var(--text3)' }}>
              Do not also enter this week in the GAMMIS portal, or it will be billed twice.
            </p>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onCancel} disabled={sending}>Cancel</button>
            <button className="btn btn-primary" onClick={onConfirm} disabled={sending}
              style={live ? { background: 'var(--danger)', borderColor: 'var(--danger)', color: 'var(--white)' } : undefined}>
              {sending ? 'Sending…' : `Send ${count} claim${count === 1 ? '' : 's'} to GAMMIS`}
            </button>
          </div>
        </div>
      </div>
  );
}

export function DeleteRunDialog({ run, deleting, error, onCancel, onDelete }) {
  return (
      <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && !deleting && onCancel()}>
        <div className="modal" style={{ maxWidth: 440 }} onMouseDown={e => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title" style={{ color: 'var(--danger)' }}>Delete Billing Run</div>
            <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={deleting}>✕</button>
          </div>
          <div className="modal-body">
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>
              This will permanently delete the billing run and all <strong>{run.total_lines}</strong> claim lines for:
            </p>
            <div style={{ padding: '10px 14px', background: 'var(--danger-bg)', borderRadius: 8, fontSize: 13, color: 'var(--danger)', fontWeight: 500, marginBottom: 8 }}>
              {fmtDate(run.date_from)} – {fmtDate(run.date_to)} · {fmt$(run.total_charge_amount)}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text3)' }}>This action cannot be undone.</p>
            {error && (
              <div role="alert" style={{ marginTop: 10, padding: '10px 14px', background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, fontSize: 12.5, color: 'var(--text)', lineHeight: 1.5 }}>
                {error}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onCancel} disabled={deleting}>Cancel</button>
            <button className="btn btn-danger" onClick={onDelete} disabled={deleting || !!error}
              style={{ background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', fontWeight: 600, cursor: 'pointer' }}>
              {deleting ? 'Deleting…' : 'Delete Run'}
            </button>
          </div>
        </div>
      </div>
  );
}

export function IcnDialog({ modal, value, onChange, saving, onCancel, onSave }) {
  return (
      <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onCancel()}>
        <div className="modal" style={{ maxWidth: 440 }} onMouseDown={e => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">{modal.payer === 'caresource' ? 'Record CareSource Claim Number' : 'Enter GAMMIS Reference / ICN'}</div>
            <button className="btn btn-ghost btn-sm" onClick={onCancel}>✕</button>
          </div>
          <div className="modal-body">
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              {modal.payer === 'caresource'
                ? 'After filing this run with CareSource, enter the claim number they returned. This marks the run as submitted.'
                : 'After uploading the CSV or 837P EDI to GAMMIS, enter the batch reference or ICN returned. This marks the run as submitted.'}
            </p>
            <div className="form-group">
              <label className="form-label">{modal.payer === 'caresource' ? 'CareSource Claim Number' : 'GAMMIS Batch Reference / ICN'} <span className="req">*</span></label>
              <input className="form-input" value={value} onChange={e => onChange(e.target.value)}
                placeholder="e.g. 2226070xxxxx" autoFocus />
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onCancel}>Cancel</button>
            <button className="btn btn-primary" onClick={onSave} disabled={saving || !value.trim()}>
              {saving ? 'Saving…' : 'Save & Mark Submitted'}
            </button>
          </div>
        </div>
      </div>
  );
}
